/**
 * Thin envelope-shaped wrapper over `sharedAxios`.
 *
 * The historical `request({ url, method, body, ... })` shape is preserved
 * so that the ~178 useQuery / useMutation callers across the codebase
 * remain untouched. Internally, the wrapper now delegates to the shared
 * axios instance which centralises:
 *
 *   - Authorization header injection (request interceptor reads tokenMemory
 *     with cookie fallback — see src/shared/api/auth.ts).
 *   - `lang` header from cookie (request interceptor).
 *   - 401 handling (response interceptor: client-side cleanup + redirect
 *     to /login; refresh-and-retry will re-activate in PR-2 when backend
 *     ships /v1/auth/refresh).
 *
 * This module previously duplicated all three concerns inline. After
 * migration to sharedAxios, the only remaining responsibility is
 * (a) JSON-serialise the body, (b) re-throw the API error envelope shape
 * the existing callers expect.
 */

import { sharedAxios } from './axios'
import { toApiError } from './api-error'

export interface RequestConfig {
  /** @deprecated Ignored; baseURL lives on the shared axios instance. */
  baseurl?: string
  url?: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  headers?: Record<string, string>
  isPost?: boolean
  /** @deprecated Ignored; the shared axios request interceptor sets `lang`. */
  lang?: string | null
  /**
   * AbortSignal forwarded to axios (native `signal` support in axios 1.x).
   * TanStack Query wrappers inject `ctx.signal` here so in-flight requests
   * cancel on unmount / queryKey change (audit/09-crosscutting.md §4).
   */
  signal?: AbortSignal
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  httpStatus?: string
  meta?: {
    total?: number
    totalElements?: number
    totalPages?: number
    page?: number
    size?: number
    [key: string]: unknown
  }
  totalCount?: number
  errorsList?: Array<{ field: string; message: string; [key: string]: unknown }>
}

export async function request<T = any>({
  url = '',
  method = 'GET',
  body = null,
  headers = {},
  isPost = true,
  signal,
}: RequestConfig): Promise<ApiResponse<T>> {
  try {
    let data: unknown = body
    const reqHeaders: Record<string, string> = { ...headers }

    if (body && isPost) {
      data = JSON.stringify(body)
      reqHeaders['Content-Type'] = 'application/json'
    }

    const response = await sharedAxios({
      url,
      method,
      data,
      headers: reqHeaders,
      signal,
    })

    return response.data as ApiResponse<T>
  } catch (error: unknown) {
    // 401 handling moved to the shared axios response interceptor
    // (src/shared/api/axios.ts). It performs client-side cleanup and
    // redirects to /login.
    //
    // Single error contract (FIX_BACKLOG #9): every rejection is an
    // `ApiError`. Envelope fields (`message`, `errorsList`, `success`, ...)
    // are preserved as own properties, so legacy `onError: (res) =>
    // res?.errorsList` consumers see the same reads as the previously
    // thrown raw `response.data` object.
    throw toApiError(error)
  }
}

/** Backward-compatible hook wrapper — returns `{ request }` */
export function useRequest() {
  return { request }
}

export default useRequest
